import { HttpParams } from '@angular/common/http'
import { Component, DestroyRef, OnInit, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import { BlueStakesLog, ListItem, RepositoryModal } from '../../shared'
import { BlueStakesLogsComponent } from './blue-stakes-logs.component'

@Component({
    templateUrl: './blue-stakes-log.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent],
})
export class BlueStakesLogComponent extends RepositoryModal<BlueStakesLog> implements OnInit {
    private destroyRef = inject(DestroyRef)
    private parentComponent!: BlueStakesLogsComponent

    faX = faX
    documents: File[] = []

    jobOptions: ListItem<string>[] = []
    requesterOptions: ListItem<number | null>[] = []
    ticketOwnerOptions: ListItem<number>[] = []

    /** Time-of-day only; Wijmo `wj-input-time` binds a `Date` (date part ignored). */
    requestTimeValue: Date | null = null

    /** Same calendar date as `timeBase`; limits picker to business hours. */
    readonly requestTimeMin = new Date(1900, 0, 1, 6, 0, 0, 0)
    readonly requestTimeMax = new Date(1900, 0, 1, 20, 0, 0, 0)

    private static readonly timeBase = new Date(1900, 0, 1, 0, 0, 0, 0)

    private previousJobId = ''

    ngOnInit() {
        this.parentComponent = this.editOptions.initializeParams.component as BlueStakesLogsComponent
        this.jobOptions = this.parentComponent.jobMap
        this.buildUserOptions()

        if (this.isAdding) {
            this.target.isActive = false
            this.target.ticketOwnerHrRefs = []
            this.target.documentUrls = []
            this.target.documentFileNames = []
            this.target.job ??= ''
            this.target.address ??= ''
            this.target.ticketNumber ??= ''
            this.target.phoneNumber ??= ''
            this.target.notes ??= ''
        } else {
            this.target.ticketOwnerHrRefs = [...(this.target.ticketOwnerHrRefs ?? [])]
        }

        if (this.isDuplicating) {
            this.target.documentUrls = []
            this.target.documentFileNames = []
        }

        this.target.documentUrls ??= []
        this.target.documentFileNames ??= []

        // Match persisted job string to Vista `VpJob.id` so the combo shows the selection (same id as ListItem.value).
        const canonicalJob = BlueStakesLogsComponent.resolveCanonicalJobId(
            this.target.job,
            this.parentComponent.activeJobs,
        )
        if (canonicalJob != null) {
            this.target.job = canonicalJob
        }

        this.previousJobId = this.target.job ?? ''

        this.syncTicketOwnerChecksFromTarget()
        this.syncRequestTimeFromTarget()
    }

    private buildUserOptions() {
        const active = this.parentComponent.activeVistaUsers ?? []
        this.requesterOptions = [
            new ListItem<number | null>(null, '(none)'),
            ...active.map(u => new ListItem<number | null>(u.id, u.name)),
        ]
        this.ticketOwnerOptions = active.map(u => new ListItem<number>(u.id, u.name))
    }

    /**
     * Wijmo multi-select does not reliably sync checked state from `[(ngModel)]` alone; drive selection via `ListItem.$checked`
     * (same pattern as HR notifications recipient groups).
     */
    private syncTicketOwnerChecksFromTarget() {
        const raw = this.target.ticketOwnerHrRefs ?? []
        const refs = new Set<number>()
        for (const x of raw) {
            if (typeof x === 'number' && Number.isFinite(x)) {
                refs.add(Math.trunc(x))
                continue
            }
            if (typeof x === 'string' && /^\d+$/.test(x)) {
                refs.add(parseInt(x, 10))
            }
        }
        for (const opt of this.ticketOwnerOptions) {
            opt.$checked = refs.has(opt.value)
        }
    }

    onJobChange() {
        const job = this.target.job ?? ''
        if (job === this.previousJobId) return
        this.previousJobId = job
        if (!job) {
            return
        }
        this.loadJobAddress(job)
    }

    onRequesterChange(hrRef: number | null) {
        if (hrRef == null) {
            return
        }
        const user = this.parentComponent.activeVistaUsers?.find(u => u.id === hrRef)
        if (user?.phone) {
            this.target.phoneNumber = user.phone
        }
    }

    private loadJobAddress(jobId: string) {
        const params = new HttpParams().set('jobId', jobId)
        this.services.data
            .get<{
                mailAddress?: string
                shipCity?: string
                shipState?: string
                hasCompleteAddress?: boolean
            }>('/api/VpJob/GetJobAddress', params)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    if (result?.hasCompleteAddress) {
                        const parts: string[] = []
                        if (result.mailAddress) parts.push(result.mailAddress)
                        if (result.shipCity) parts.push(result.shipCity)
                        if (result.shipState) parts.push(result.shipState)
                        this.target.address = parts.join(', ')
                    }
                },
            })
    }

    /**
     * Wijmo multi-select may replace `ticketOwnerHrRefs` with `ListItem`-shaped objects instead of plain numbers.
     * The API expects `number[]` for JSON deserialization into `List<int>`.
     */
    private static normalizeHrRefArray(raw: unknown): number[] {
        if (raw == null || !Array.isArray(raw)) return []
        const out: number[] = []
        for (const x of raw) {
            if (typeof x === 'number' && Number.isFinite(x)) {
                out.push(Math.trunc(x))
                continue
            }
            if (typeof x === 'object' && x !== null && 'value' in x) {
                const v = (x as { value: unknown }).value
                if (typeof v === 'number' && Number.isFinite(v)) {
                    out.push(Math.trunc(v))
                } else if (typeof v === 'string' && /^\d+$/.test(v)) {
                    out.push(parseInt(v, 10))
                }
            }
        }
        return out
    }

    /** Snap to 15 minutes and clamp to picker range (6:00 AM–8:00 PM). */
    private static clampBusinessMinutes(total: number): number {
        const minM = 6 * 60
        const maxM = 20 * 60
        let t = Math.round(total / 15) * 15
        return Math.max(minM, Math.min(maxM, t))
    }

    private syncRequestTimeFromTarget() {
        const t = this.target.requestTime
        if (t == null || t === '') {
            this.requestTimeValue = null
            return
        }
        const s = typeof t === 'string' ? t : String(t)
        const parts = s.split(':')
        if (parts.length >= 2) {
            const h = parseInt(parts[0], 10)
            const m = parseInt(parts[1], 10)
            if (!Number.isNaN(h) && !Number.isNaN(m)) {
                const clamped = BlueStakesLogComponent.clampBusinessMinutes(h * 60 + m)
                const d = new Date(BlueStakesLogComponent.timeBase)
                d.setHours(Math.floor(clamped / 60), clamped % 60, 0, 0)
                this.requestTimeValue = d
                return
            }
        }
        this.requestTimeValue = null
    }

    protected onBeforeSave(): boolean {
        if (typeof this.target.job === 'string') {
            this.target.job = this.target.job.trim()
        }
        const canonicalJob = BlueStakesLogsComponent.resolveCanonicalJobId(
            this.target.job,
            this.parentComponent.activeJobs,
        )
        if (canonicalJob != null) {
            this.target.job = canonicalJob
        }

        if (this.requestTimeValue) {
            const d = this.requestTimeValue
            const clamped = BlueStakesLogComponent.clampBusinessMinutes(d.getHours() * 60 + d.getMinutes())
            const h = Math.floor(clamped / 60)
            const min = clamped % 60
            const aligned = new Date(BlueStakesLogComponent.timeBase)
            aligned.setHours(h, min, 0, 0)
            this.requestTimeValue = aligned
            this.target.requestTime = `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}:00`
        } else {
            this.target.requestTime = undefined
        }

        this.target.ticketOwnerHrRefs = BlueStakesLogComponent.normalizeHrRefArray(
            this.ticketOwnerOptions.filter(o => o.$checked).map(o => o.value),
        )

        if (this.target.requesterHrRef == null) {
            this.target.requesterHrRef = undefined
        }

        return true
    }

    onDocumentChanged(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files) return
        const files = input.files
        for (let i = 0; i < files.length; i++) {
            this.documents.push(files[i])
        }
        input.value = ''
    }

    removeDocument(doc: File) {
        const idx = this.documents.indexOf(doc)
        if (idx > -1) this.documents.splice(idx, 1)
    }

    isPdf(url: string): boolean {
        return url.toLowerCase().endsWith('.pdf')
    }

    getExistingFileSrc(url: string): string {
        return './Resources/Images/BlueStakesLog/' + url
    }

    /**
     * Stored URL shape: `{logId}/{attachmentId}{extension}` — returns attachmentId GUID string.
     */
    private static documentIdFromStoredUrl(url: string): string | null {
        const parts = url.split('/').filter(Boolean)
        if (parts.length < 2) return null
        const file = parts[1]
        const dot = file.lastIndexOf('.')
        const idPart = dot > 0 ? file.slice(0, dot) : file
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idPart)
            ? idPart
            : null
    }

    openDocument(url: string) {
        const docId = BlueStakesLogComponent.documentIdFromStoredUrl(url)
        if (docId) {
            window.open(`api/BlueStakesLog/DownloadDocument?documentId=${encodeURIComponent(docId)}`, '_blank')
            return
        }
        window.open(this.getExistingFileSrc(url), '_blank')
    }

    /** Original file name when provided by API; otherwise derived from stored path. */
    displayDocumentName(index: number): string {
        const names = this.target.documentFileNames
        const fromApi = names?.[index]
        if (fromApi) return fromApi
        const url = this.target.documentUrls[index]
        return url?.split('/').pop() ?? ''
    }

    protected override onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: successResult => {
                if (this.documents.length === 0) {
                    resolveSave(successResult)
                    return
                }

                const uploadData = new FormData()
                uploadData.append('id', successResult.id)
                for (let i = 0; i < this.documents.length; i++) {
                    uploadData.append('myFile' + i, this.documents[i], this.documents[i].name)
                }
                this.services.data
                    .post('api/BlueStakesLog/UploadImages', uploadData)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: () => resolveSave(successResult),
                        error: errorResult => rejectSave(errorResult),
                    })
            },
            error: errorResult => rejectSave(errorResult),
        })
    }
}
